/**
 * Workflow Adapter
 * Transforms workflow data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type {
	Workflow,
	WorkflowSummary,
	PaginationMeta,
	CreateWorkflowDto,
	UpdateWorkflowDto,
	WorkflowResponseDto,
	WorkflowsListResponseDto,
	WorkflowTool,
	WorkflowAutomationWorkingHours,
	UpdateChannelsDto,
	VectorSyncStatus
} from '@/types';

export class WorkflowAdapter extends BaseAdapter {
	private static toNumber(value: number | string): number {
		if (typeof value === 'number') return value;
		const parsed = Number(value);
		return Number.isNaN(parsed) ? 0 : parsed;
	}

	/**
	 * Adapts workflow from API response
	 */
	static fromApi(dto: WorkflowResponseDto): Workflow {
		const tools: WorkflowTool[] = (dto.tools || []).map((tool) => {
			// Handle both old format { name, config: { actions }, enabled } and new format { name, actions }
			const actions = tool.actions || tool.config?.actions || [];
			return {
				name: tool.name || '',
				actions: Array.isArray(actions) ? actions : []
			};
		});

		const automationWorkingHours: WorkflowAutomationWorkingHours | undefined = dto.automationWorkingHours
			? {
					enabled: dto.automationWorkingHours.enabled,
					timezone: dto.automationWorkingHours.timezone,
					schedule: dto.automationWorkingHours.schedule,
					fallback: dto.automationWorkingHours.fallback,
					weeklySchedule: dto.automationWorkingHours.weeklySchedule
				}
			: undefined;

		// Transform enabledChannels - handle both string[] and object[] formats
		const enabledChannels: string[] = (dto.enabledChannels || [])
			.map((channel) => {
				if (typeof channel === 'string') {
					return channel;
				}
				// If it's an object, extract the ID (prefer id, then _id)
				return channel.id || channel._id || '';
			})
			.filter(Boolean); // Remove any empty strings

		return {
			id: dto.id ?? dto._id ?? '',
			accountId: WorkflowAdapter.toNumber(dto.accountId),
			name: dto.name,
			description: dto.description,
			instructions: dto.instructions,
			label: dto.label,
			expressions: dto.expressions || [],
			category: dto.category,
			subcategory: dto.subcategory,
			type: dto.type,
			tools,
			intents: dto.intents || [],
			status: dto.status ?? true,
			enabledChannels,
			automationWorkingHours,
			triggeredAt: dto.triggeredAt,
			triggerCount: dto.triggerCount ?? 0,
			brandId: dto.brandId,
			editorData: dto.editorData,
			createdAt: dto.createdAt,
			updatedAt: dto.updatedAt,
			seg: typeof dto.seg === 'string' ? JSON.parse(dto.seg) : dto.seg,
			segCompiledAt: dto.segCompiledAt,
			editorDataUpdatedAt: dto.editorDataUpdatedAt ?? null,
			compilationStatus: dto.compilationStatus,
			compilationError: dto.compilationError ?? null,
			sopId: dto.sopId ?? null,
			ticketIds: dto.ticketIds ?? [],
			vectorSyncStatus: (dto.vectorSyncStatus as VectorSyncStatus | undefined) ?? 'pending',
			vectorSyncedAt: dto.vectorSyncedAt ?? null,
			vectorSyncAttemptedAt: dto.vectorSyncAttemptedAt ?? null,
			vectorSyncError: dto.vectorSyncError ?? null
		};
	}

	/**
	 * Adapts workflows array from API response
	 */
	static fromApiList(dtos: WorkflowResponseDto[]): Workflow[] {
		return dtos.map((dto) => this.fromApi(dto));
	}

	/**
	 * Adapts workflows list response
	 */
	static fromListApi(dto: WorkflowsListResponseDto): { workflows: Workflow[]; count: number } {
		return {
			workflows: this.fromApiList(dto.workflows || []),
			count: dto.count || 0
		};
	}

	/**
	 * Adapts a single workflow DTO to WorkflowSummary (list view — no heavy fields)
	 */
	static fromSummaryApi(dto: WorkflowResponseDto): WorkflowSummary {
		const enabledChannels: string[] = (dto.enabledChannels || [])
			.map((channel) => {
				if (typeof channel === 'string') return channel;
				return channel.id || channel._id || '';
			})
			.filter(Boolean);

		return {
			id: dto.id ?? dto._id ?? '',
			accountId: WorkflowAdapter.toNumber(dto.accountId),
			name: dto.name,
			description: dto.description,
			label: dto.label,
			type: dto.type,
			status: dto.status ?? true,
			compilationError: dto.compilationError ?? null,
			compilationStatus: dto.compilationStatus,
			enabledChannels,
			category: dto.category,
			subcategory: dto.subcategory,
			categorizeMiss: dto.categorizeMiss,
			triggerCount: dto.triggerCount ?? 0,
			createdAt: dto.createdAt,
			updatedAt: dto.updatedAt,
			vectorSyncStatus: (dto.vectorSyncStatus as VectorSyncStatus | undefined) ?? 'pending',
			vectorSyncedAt: dto.vectorSyncedAt ?? null,
			vectorSyncAttemptedAt: dto.vectorSyncAttemptedAt ?? null,
			vectorSyncError: dto.vectorSyncError ?? null
		};
	}

	/**
	 * Adapts paginated workflows list response to WorkflowSummary[]
	 */
	static fromSummaryListApi(dto: WorkflowsListResponseDto): { workflows: WorkflowSummary[]; pagination: PaginationMeta } {
		return {
			workflows: (dto.workflows || []).map((w) => this.fromSummaryApi(w)),
			pagination: {
				page: dto.pagination?.page ?? 1,
				limit: dto.pagination?.limit ?? 20,
				totalPages: dto.pagination?.totalPages ?? 1,
				total: dto.pagination?.total ?? dto.count ?? 0
			}
		};
	}

	/**
	 * Adapts create workflow request
	 */
	static toCreateRequest(data: CreateWorkflowDto): CreateWorkflowDto {
		const payload: CreateWorkflowDto = {
			name: data.name.trim(),
			type: data.type,
			accountId: data.accountId,
			description: data.description?.trim(),
			instructions: data.instructions?.trim(),
			label: data.label?.trim(),
			expressions: data.expressions,
			category: data.category?.trim(),
			subcategory: data.subcategory?.trim(),
			tools: data.tools,
			intents: data.intents,
			automationWorkingHours: data.automationWorkingHours,
			editorData: data.editorData
		};

		return this.sanitizePayload(payload);
	}

	/**
	 * Adapts update workflow request
	 */
	static toUpdateRequest(data: UpdateWorkflowDto): Record<string, unknown> {
		const payload: Record<string, unknown> = {
			name: data.name?.trim(),
			type: data.type,
			accountId: data.accountId,
			description: data.description?.trim(),
			instructions: data.instructions?.trim(),
			label: data.label?.trim(),
			expressions: data.expressions,
			category: data.category?.trim(),
			subcategory: data.subcategory?.trim(),
			tools: data.tools,
			intents: data.intents,
			status: data.status,
			enabledChannels: data.enabledChannels,
			automationWorkingHours: data.automationWorkingHours,
			triggeredAt: data.triggeredAt,
			triggerCount: data.triggerCount,
			brandId: data.brandId,
			editorData: data.editorData,
			is_generate_graph: data.isGenerateGraph,
			compile_guided: data.compileGuided,
			compilationStatus: data.compilationStatus
		};

		// After sanitizing, which drops nulls: a null base ("never saved") is still a base to check.
		return {
			...this.sanitizePayload(payload),
			...(data.baseEditorDataUpdatedAt !== undefined && { baseEditorDataUpdatedAt: data.baseEditorDataUpdatedAt })
		};
	}

	/**
	 * Adapts update channels request
	 */
	static toUpdateChannelsRequest(enabledChannels: string[]): UpdateChannelsDto {
		return { enabledChannels };
	}
}
